import React from "react";
import {
  KG_INSPECTOR_ICON_DEFAULT_SIZE,
  kgInspectorIcons,
} from "../../kg/canvas/inspector/kgInspectorIcons";
import { KgInspectorDataGrid } from "../KgInspectorDataGrid";
import { KgInspectorDetailRow } from "../KgInspectorDetailRow";
import {
  ArtifactsBlock,
  DescriptionBody,
  DetailValue,
  EmptyHint,
  IconSlot,
  OverviewRoot,
  SectionBlock,
  SectionHeader,
  SectionTitle,
  SharedWithItem,
  SharedWithList,
} from "./KgInspectorDataStoryOverview.styles";
import type { KgInspectorDataStoryOverviewProps } from "./KgInspectorDataStoryOverview.types";

const SummaryIcon = kgInspectorIcons.description;
const OwnerIcon = kgInspectorIcons.owner;
const SharedWithIcon = kgInspectorIcons.sharedWith;
const ArtifactsIcon = kgInspectorIcons.artifacts;

export const KgInspectorDataStoryOverview: React.FC<
  KgInspectorDataStoryOverviewProps
> = ({
  overview,
  isArtifactsOpen = true,
  onToggleArtifacts,
  summaryEmptyHint = "No summary available for this data story yet.",
}) => {
  const summary = overview?.summary ?? "";
  const owner = overview?.owner ?? "";
  const sharedWith = overview?.sharedWith ?? [];
  const artifacts = overview?.artifacts ?? [];

  return (
    <OverviewRoot>
      <SectionBlock>
        <SectionHeader>
          <IconSlot>
            <SummaryIcon size={16} />
          </IconSlot>
          <SectionTitle>Summary</SectionTitle>
        </SectionHeader>
        {summary ? (
          <DescriptionBody>{summary}</DescriptionBody>
        ) : (
          <EmptyHint>{summaryEmptyHint}</EmptyHint>
        )}
      </SectionBlock>

      {owner ? (
        <KgInspectorDetailRow
          icon={
            <OwnerIcon size={KG_INSPECTOR_ICON_DEFAULT_SIZE.owner} />
          }
          label="Owner"
          value={<DetailValue>{owner}</DetailValue>}
        />
      ) : null}

      {sharedWith.length > 0 ? (
        <KgInspectorDetailRow
          icon={
            <SharedWithIcon
              size={KG_INSPECTOR_ICON_DEFAULT_SIZE.sharedWith}
            />
          }
          label="Shared with"
          value={
            <SharedWithList>
              {sharedWith.map((entry) => (
                <SharedWithItem key={entry}>{entry}</SharedWithItem>
              ))}
            </SharedWithList>
          }
        />
      ) : null}

      <KgInspectorDetailRow
        icon={
          <ArtifactsIcon size={KG_INSPECTOR_ICON_DEFAULT_SIZE.artifacts} />
        }
        label="Artifacts"
        badge={artifacts.length > 0 ? artifacts.length : undefined}
        isOpen={isArtifactsOpen}
        onToggle={onToggleArtifacts}
        value={null}
      />

      {isArtifactsOpen && (
        <ArtifactsBlock>
          {artifacts.length === 0 ? (
            <EmptyHint>No artifacts linked to this data story yet.</EmptyHint>
          ) : (
            <KgInspectorDataGrid
              rows={artifacts}
              rowKey={(row) => row.id}
              columns={[
                {
                  key: "format",
                  header: "FORMAT",
                  width: "narrow",
                  render: (row) => row.format,
                },
                {
                  key: "label",
                  header: "LABEL",
                  width: "flex",
                  render: (row) => row.label,
                },
                {
                  key: "publishedOn",
                  header: "PUBLISHED ON",
                  width: "equal",
                  render: (row) => row.publishedOn,
                },
              ]}
            />
          )}
        </ArtifactsBlock>
      )}
    </OverviewRoot>
  );
};

export default KgInspectorDataStoryOverview;
